/**
 * UI configuration for languages and categories.
 * These are presentation-layer concerns that don't belong in the rules data.
 */

export interface LanguageUIConfig {
  icon: string;
  color: string;
  status?: 'beta' | 'coming-soon';
}

export interface CategoryUIConfig {
  icon?: string;
  description?: string;
}

/**
 * UI configuration for languages (icons, colors, status).
 */
export const LANGUAGE_UI_CONFIG: Record<string, LanguageUIConfig> = {
  'docker': {
    icon: 'docker',
    color: 'from-blue-400 to-blue-600',
  },
  'docker-compose': {
    icon: 'docker',
    color: 'from-cyan-400 to-blue-500',
  },
  'python': {
    icon: 'python',
    color: 'from-blue-500 to-green-500',
  },
  'c': {
    icon: 'c',
    color: 'from-blue-500 to-blue-700',
    status: 'coming-soon',
  },
  'cpp': {
    icon: 'cpp',
    color: 'from-blue-600 to-indigo-700',
    status: 'coming-soon',
  },
  'golang': {
    icon: 'golang',
    color: 'from-[#00ADD8] to-[#007d9c]',
  },
  // Default fallback
  'default': {
    icon: 'code',
    color: 'from-gray-400 to-gray-600',
  },
};

/**
 * UI configuration for categories (icons, descriptions).
 */
export const CATEGORY_UI_CONFIG: Record<string, CategoryUIConfig> = {
  'security': {
    icon: 'shield',
    description: 'Critical security rules for preventing vulnerabilities',
  },
  'best-practice': {
    icon: 'check',
    description: 'Best practices and optimization rules',
  },
  'best-practices': {
    icon: 'check',
    description: 'Best practices and optimization rules',
  },
  'correctness': {
    icon: 'alert',
    description: 'Rules that catch build errors and correctness issues',
  },
  'audit': {
    icon: 'document',
    description: 'Audit and compliance rules',
  },
  'owasp-top-10': {
    icon: 'shield',
    description: 'OWASP Top 10 security vulnerabilities',
  },
  'performance': {
    icon: 'lightning',
    description: 'Performance and optimization rules',
  },
  'networking': {
    icon: 'network',
    description: 'Network configuration and security',
  },
  'django': {
    icon: 'shield',
    description: 'Security rules for Django web framework',
  },
  'flask': {
    icon: 'shield',
    description: 'Security rules for Flask web framework',
  },
  'deserialization': {
    icon: 'alert',
    description: 'Unsafe deserialization and data integrity vulnerabilities',
  },
};

/**
 * Get UI configuration for a language.
 */
export function getLanguageUIConfig(languageId: string): LanguageUIConfig {
  return LANGUAGE_UI_CONFIG[languageId] || LANGUAGE_UI_CONFIG['default'];
}

/**
 * Get UI configuration for a category.
 */
export function getCategoryUIConfig(categoryId: string): CategoryUIConfig {
  return CATEGORY_UI_CONFIG[categoryId] || {};
}
